/**
 * One read per identity, remembered for the page's lifetime (D11b).
 *
 * THE ANSWER IS STORED UNDER THE IDENTITY IT WAS ASKED FOR. The run history and
 * the run detail both had, or would have had, the D9 finding: a hook that keeps
 * ONE answer shows, for the render between a selection change and the next
 * request, the previous automation's runs under the new automation's name
 * (D9 finding 3). Here an answer is filed under `id` — an automation and a
 * page, or an automation and a run — and only the entry for the CURRENT id is
 * ever handed out. Until it exists, the caller reads "loading", never someone
 * else's data. A late answer for another id is not discarded, it is filed
 * where it belongs; it simply is not what is on screen.
 *
 * NEWEST REQUEST PER ID WINS. Two requests for the same id can be in flight (a
 * retry while the first still hangs); each carries a number, and only the
 * latest number for that id may write. An older answer arriving last cannot
 * overwrite a newer one.
 *
 * A CACHE FOR THE PAGE, NOT FOR THE BROWSER. The entries live in this hook's
 * state: leaving the page drops them, a reload starts empty, nothing reaches
 * storage. Reopening a tab or going back to a run that was already read costs
 * nothing; a failed read is never cached — opening it again asks again.
 *
 * `refresh(predicate)` FORGETS, `reload()` RE-ASKS. `refresh` drops every entry
 * whose id matches (and lets any of their answers still in flight fall on the
 * floor), then asks again for the current id if it was among them. `reload`
 * asks again for the current id only — the retry after a failure.
 *
 * `load` IS READ THROUGH A REF, and the effect depends on `active` and `id`
 * alone. Depending on the loader would re-run the effect on every render;
 * depending on the entries would re-ask in a loop after a failure. The refs are
 * written after COMMIT (a layout effect), never during render, and the loader
 * is called synchronously by whoever starts the request — so the loader that
 * runs is always the one of the render that named this id, and an answer can
 * never be filed under an id it was not asked for.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export default function useCachedRead({ active, id, load }) {
  const [entries, setEntries] = useState(() => new Map());
  const entriesRef = useRef(entries);
  const loadRef = useRef(load);
  const current = useRef({ active, id });
  useLayoutEffect(() => {
    entriesRef.current = entries;
    loadRef.current = load;
    current.current = { active, id };
  });

  // id → the number of the newest request for it. An id is "in flight" while
  // it is in this map.
  const latest = useRef(new Map());
  const counter = useRef(0);

  const start = useCallback((target) => {
    counter.current += 1;
    const mine = counter.current;
    latest.current.set(target, mine);
    setEntries((prev) => new Map(prev).set(target, { status: "loading" }));
    const settle = (entry) => {
      if (latest.current.get(target) !== mine) return;
      latest.current.delete(target);
      setEntries((prev) => new Map(prev).set(target, entry));
    };
    let request;
    try {
      request = Promise.resolve(loadRef.current());
    } catch (error) {
      request = Promise.reject(error);
    }
    request.then(
      (value) => settle({ status: "ready", value }),
      (error) => settle({ status: "error", error })
    );
  }, []);

  useEffect(() => {
    if (!active || !id) return;
    if (latest.current.has(id)) return;
    const entry = entriesRef.current.get(id);
    if (entry && entry.status === "ready") return;
    start(id);
  }, [active, id, start]);

  const reload = useCallback(() => {
    const now = current.current;
    if (now.active && now.id) start(now.id);
  }, [start]);

  const refresh = useCallback(
    (predicate) => {
      for (const key of [...latest.current.keys()]) {
        if (predicate(key)) latest.current.delete(key);
      }
      setEntries((prev) => {
        const next = new Map();
        for (const [key, entry] of prev) {
          if (!predicate(key)) next.set(key, entry);
        }
        return next;
      });
      const now = current.current;
      if (now.active && now.id && predicate(now.id)) start(now.id);
    },
    [start]
  );

  // What is known about the current id is handed out even while inactive: a
  // tab that stays mounted behind another one keeps showing what it read, and
  // it is still keyed — it can only ever be this id's answer. Only "loading"
  // is reserved for an active read.
  const entry = id ? entries.get(id) : undefined;
  return {
    value: entry && entry.status === "ready" ? entry.value : null,
    error: entry && entry.status === "error" ? entry.error : null,
    loading: !!(active && id) && (!entry || entry.status === "loading"),
    reload,
    refresh,
  };
}
